// @ts-strict-ignore
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { get, memoize, pick } from 'lodash-es';
import { DEFAULT_LANGUAGE } from '../../app.constants';
import { applyDefaultValues } from '../../functions/utilities/apply-default-values';
import { RecursivePropOfBoard } from './board.actions';
import { boardViewModelDefaultValues } from './board.selector.constants';
import { BoardState, BoardViewModel } from './board.types';

export const selectBoardState = createFeatureSelector<BoardState>('board');

export const selectBoard = createSelector(selectBoardState, (state) => state.board);

export const selectBoardLastSaved = createSelector(selectBoardState, (state) => state.boardLastSaved);

export const selectIsBoardLoading = createSelector(selectBoardState, (state) => state.boardLoading);

export const selectIsBoardLoadingError = createSelector(selectBoardState, (state) => state.boardLoadingError);

export const selectIsBoardSaving = createSelector(selectBoardState, (state) => state.boardSaving);

export const selectBoardSavingError = createSelector(selectBoardState, (state) => state.boardSaveError);

export const selectBoardProperty = memoize((property: RecursivePropOfBoard) =>
  createSelector(selectBoard, (board) => (!board ? null : get(board, property)))
);

const boardTabs = createSelector(selectBoard, (board): BoardViewModel['board-tabs'] => {
  if (!board) {
    return null;
  }
  return {
    ...pick(board, ['_etag', 'id', 'title', 'supplyChildNodes']),
    boardSettings: {
      ...pick(board.boardSettings, [
        'allowEmissionView',
        'totalEmissions',
        'internationalisation',
        'primaryActionGrouping',
        'secondaryActionGrouping',
      ]),
    },
  };
});

const homeProgressOverview = createSelector(selectBoard, (board): BoardViewModel['home/progress-overview'] => {
  if (!board) {
    return null;
  }
  return {
    ...pick(board, ['id', 'title']),
    boardSettings: {
      ...pick(board.boardSettings, [
        'swimlanes',
        'displayUnit',
        'reductionGoals',
        'timeline',
        'electricityEmissionFactorType',
      ]),
    },
  };
});

const homeWatchlist = createSelector(selectBoard, (board): BoardViewModel['home/watchlist'] => {
  if (!board) {
    return null;
  }
  return {
    boardSettings: { ...pick(board.boardSettings, ['swimlanes']) },
  };
});

const inventoryGoal = createSelector(selectBoard, (board): BoardViewModel['inventory/goal'] => {
  if (!board) {
    return null;
  }
  const uncheckedGoalValues = pick(board, 'boardSettings');
  const checkedGoalValues = applyDefaultValues(uncheckedGoalValues, boardViewModelDefaultValues['inventory/goal']);

  const ratifiedDate = checkedGoalValues.boardSettings.reductionGoals?.[0]?.ratifiedDate;
  if (ratifiedDate) {
    checkedGoalValues.boardSettings.reductionGoals[0].ratifiedDate = new Date(ratifiedDate);
  }

  return {
    ...checkedGoalValues,
  };
});

const pathwaysTransitionElements = createSelector(
  selectBoard,
  (board): BoardViewModel['pathways/transition-elements'] => {
    if (!board) {
      return null;
    }
    return {
      ...pick(board, ['id', 'title', 'childNodes', 'supplyChildNodes']),
      boardSettings: {
        ...pick(board.boardSettings, [
          'timeline',
          'displayUnit',
          'measurementSystem',
          'electricityEmissionFactorType',
          'internationalisation',
        ]),
        pointOfEntrySettings: {
          header: {
            ...pick(board.boardSettings.pointOfEntrySettings.header, ['country', 'tagline']),
          },
        },
      },
    };
  }
);

const pathwaysTargetBalance = createSelector(selectBoard, (board): BoardViewModel['pathways/target-balance'] => {
  if (!board) {
    return null;
  }
  return {
    ...pick(board, ['id', 'supplyChildNodes']),
    boardSettings: {
      ...pick(board.boardSettings, [
        'currency',
        'measurementSystem',
        'stakeholders',
        'timeline',
        'electricityEmissionFactorType',
      ]),
    },
  };
});

export const pathwaysEnergyModel = createSelector(selectBoard, (board): BoardViewModel['pathways/energy-model'] => {
  if (!board) {
    return null;
  }

  return {
    ...pick(board, ['title', 'id', 'supplyChildNodes', 'childNodes']),
    boardSettings: {
      ...pick(board.boardSettings, ['timeline', 'displayUnit', 'electricityEmissionFactorType', 'measurementSystem']),
      pointOfEntrySettings: {
        header: {
          ...pick(board.boardSettings.pointOfEntrySettings.header, ['country', 'tagline']),
        },
      },
    },
  };
});

const pathwaysTransitionHierarchy = createSelector(
  selectBoard,
  (board): BoardViewModel['pathways/transition-hierarchy'] => {
    if (!board) {
      return null;
    }
    const uncheckedSettings = pick(board, 'boardSettings');
    const checkedSettings = applyDefaultValues(
      uncheckedSettings,
      boardViewModelDefaultValues['pathways/transition-hierarchy']
    );
    return {
      id: board.id,
      title: board.title,
      childNodes: board.childNodes,
      supplyChildNodes: board.supplyChildNodes,
      parameters: board.parameters,
      _etag: board._etag,
      _ts: board._ts,
      ...checkedSettings,
    };
  }
);

const actionsActions = createSelector(selectBoard, (board): BoardViewModel['actions/actions'] => {
  if (!board) {
    return null;
  }
  return {
    ...pick(board, ['id', 'title', 'childNodes', 'supplyChildNodes']),
    boardSettings: {
      ...pick(board.boardSettings, [
        'actionOrigins',
        'actionTypes',
        'timeline',
        'swimlanes',
        'internationalisation',
        'auxiliaryActionProperties',
        'imgURL',
        'displayUnit',
        'transitionTargetNodes',
        'doNotDistributeUntargeted',
        'unaddressedEmissionsDisplay',
        'useNewTargetedEmissions',
        'uploadImageForActionsEnabled',
        'showActionsCosts',
        'actionTags',
        'stakeholders',
        'measurementSystem',
        'currency',
        'actionTags',
        'electricityEmissionFactorType',
        'publicTabs',
      ]),
      pointOfEntrySettings: {
        header: {
          ...pick(board.boardSettings.pointOfEntrySettings.header, ['country', 'tagline']),
        },
      },
    },
  };
});

const actionsActionSettings = createSelector(selectBoard, (board): BoardViewModel['actions/action-settings'] => {
  if (!board) {
    return null;
  }
  return {
    boardSettings: {
      actionTypes: board.boardSettings?.actionTypes ?? [],
      swimlanes: board.boardSettings?.swimlanes ?? [],
      actionTags: board.boardSettings?.actionTags ?? [],
      actionOrigins: board.boardSettings?.actionOrigins ?? [],
    },
  };
});

const interventionsInterventionLibraries = createSelector(
  selectBoard,
  (board): BoardViewModel['interventions/intervention-libraries'] => {
    return {
      boardSettings: {
        actionTypes: board?.boardSettings?.actionTypes ?? [],
        swimlanes: board?.boardSettings?.swimlanes ?? [],
        internationalisation: board?.boardSettings?.internationalisation ?? { defaultLanguage: DEFAULT_LANGUAGE },
      },
    };
  }
);

const investmentPlan = createSelector(selectBoard, (board): BoardViewModel['investment-plan'] => {
  if (!board) {
    return null;
  }
  return {
    // Needed for ClientBoardContainer | '_etag' | '_ts'
    ...pick(board, ['id', 'title', 'childNodes', 'supplyChildNodes', 'parameters', '_etag', '_ts']),
    boardSettings: {
      ...pick(board.boardSettings, [
        'timeline',
        'extendedDescription',
        'displayUnit',
        'swimlanes',
        'stakeholders',
        'measurementSystem',
        'currency',
        // Needed for ClientBoardContainer
        'internationalisation',
        'auxiliaryActionProperties',
        'imgURL',
        'transitionTargetNodes',
        'doNotDistributeUntargeted',
        'unaddressedEmissionsDisplay',
        'useNewTargetedEmissions',
        'electricityEmissionFactorType',
        'publicTabs',
      ]),
    },
  };
});

const dashboardStories = createSelector(selectBoard, (board): BoardViewModel['dashboard/stories'] => {
  if (!board) {
    return null;
  }
  const uncheckedSettings = pick(board, 'boardSettings');
  const checkedSettings = applyDefaultValues(uncheckedSettings, boardViewModelDefaultValues['dashboard/stories']);
  return {
    id: board.id,
    title: board.title,
    _etag: board._etag,
    childNodes: board.childNodes,
    supplyChildNodes: board.supplyChildNodes,
    ...checkedSettings,
  };
});

const inventoryExportImport = createSelector(selectBoard, (board): BoardViewModel['inventory/export-import'] => {
  if (!board) {
    return null;
  }
  return {
    id: board.id,
    boardSettings: {
      internationalisation: board.boardSettings.internationalisation,
      timeline: board.boardSettings.timeline,
    },
  };
});

const dataViewModules = createSelector(selectBoard, (board): BoardViewModel['data-view-modules'] => {
  if (!board) {
    return null;
  }
  return {
    boardSettings: { ...pick(board.boardSettings, ['timeline', 'displayUnit', 'measurementSystem']) },
  };
});

const settingsGeneral = createSelector(selectBoard, (board): BoardViewModel['settings/general'] => {
  if (!board) {
    return null;
  }
  return {
    title: board.title,
    boardSettings: {
      currency: board.boardSettings.currency,
      electricityEmissionFactorType: board.boardSettings.electricityEmissionFactorType,
    },
  };
});

const settingsTimeline = createSelector(selectBoard, (board): BoardViewModel['settings/timeline'] => {
  if (!board) {
    return null;
  }
  return {
    boardSettings: {
      timeline: board.boardSettings.timeline,
    },
  };
});

const settingsDashboard = createSelector(selectBoard, (board): BoardViewModel['settings/dashboard'] => {
  if (!board) {
    return null;
  }

  return {
    boardSettings: {
      ...pick(board.boardSettings, [
        'internationalisation',
        'feedbackSettings',
        'pointOfEntrySettings',
        'unaddressedEmissionsDisplay',
        'yearByYearDiagramLayout',
        'useNewTargetedEmissions',
        'transitionTargetNodes',
        'defaultActionsView',
        'showUnconnectedActions',
        'showActionsCosts',
        'publicTabs',
        'primaryActionGrouping',
        'secondaryActionGrouping',
        'actionTags',
        'actionOrigins',
        'actionSettingsSubHeading',
        'actionSettingsDescription',
        'emissionsTabHideBracket',
        'emissionsTabHideReference',
      ]),
    },
  };
});

export const selectBoardViewModel = {
  'board-tabs': boardTabs,
  'home/watchlist': homeWatchlist,
  'home/progress-overview': homeProgressOverview,
  'inventory/export-import': inventoryExportImport,
  'inventory/goal': inventoryGoal,
  'pathways/transition-elements': pathwaysTransitionElements,
  'pathways/target-balance': pathwaysTargetBalance,
  'pathways/energy-model': pathwaysEnergyModel,
  'pathways/transition-hierarchy': pathwaysTransitionHierarchy,
  'actions/actions': actionsActions,
  'actions/action-settings': actionsActionSettings,
  'interventions/intervention-libraries': interventionsInterventionLibraries,
  'investment-plan': investmentPlan,
  'dashboard/stories': dashboardStories,
  'dashboard/emission-stories': dashboardStories,
  'data-view-modules': dataViewModules,
  'settings/general': settingsGeneral,
  'settings/timeline': settingsTimeline,
  'settings/dashboard': settingsDashboard,
};
